import { useState, useEffect } from 'react';
import { generateClient, graphqlOperation } from 'aws-amplify/api';
import {
  onCreateChatMessage,
  onUpdateUserStatus,
  onDeleteTransaction,
  onCreateTransactionVideoConference,
  onDeleteTransactionVideoConference,
  onUpdateTransactionVideoConference 
} from '../graphql/subscriptions';
import {
  listUserStatuses,
  listTransactionVideoConferences
} from '../graphql/queries';

const client = generateClient();

const handleSubscription = (client, query, callback, setData) => {
  return client.graphql({ query }).subscribe({
    next: ({ data }) => {
      const transaction = data[Object.keys(data)[0]];
      callback(transaction, setData);
    },
    error: (error) => {
      console.warn('Subscription error:', error);
    },
  });
};


const handleCRUD = (client, subscriptions, setData, onExternalDelete,setLatestCreatedItem) => {
  const subscriptionsHandlers = {
    // create: (transaction) => {
    //   setData((prevData) => [transaction, ...prevData]);
    // },
    create: (transaction) => {
      setData((prevData) => {
        if (prevData.some((item) => item.id === transaction.id)) {
          return prevData; // Already exists
        }
        return [transaction, ...prevData];
      });

      setLatestCreatedItem(transaction); // <-- set the newly created one
    },
    update: (transaction) => {
      setData((prevData) =>
        prevData.map((t) => (t.id === transaction.id ? transaction : t))
      );
    },
    delete: (transaction) => {
      console.log("💥 Item deleted via subscription:", transaction);
      setData((prevData) => prevData.filter((t) => t.id !== transaction.id));
      if (onExternalDelete) {
        onExternalDelete(transaction);
      }
    },
  };

  const createSub = subscriptions?.onCreate
    ? handleSubscription(client, subscriptions.onCreate, subscriptionsHandlers.create, setData)
    : null;
  const updateSub = subscriptions?.onUpdate
    ? handleSubscription(client, subscriptions.onUpdate, subscriptionsHandlers.update, setData)
    : null;
  const deleteSub = subscriptions?.onDelete
    ? handleSubscription(client, subscriptions.onDelete, subscriptionsHandlers.delete, setData)
    : null;

  return () => {
    if (createSub) createSub.unsubscribe();
    if (updateSub) updateSub.unsubscribe();
    if (deleteSub) deleteSub.unsubscribe();
  };
};

const useModelData = ({
  model,
  queries = null,
  subscriptions,
  filter = {},
  sortDirection = "DESC",
  sortField = "createdAt"
}) => {
  const [dataAmplify, setData] = useState([]);
  const [loadingAmplify, setLoading] = useState(true);
  const [dynamicFilter, setDynamicFilter] = useState(filter || {});
  const [onExternalDelete, setOnExternalDelete] = useState(null);
const [latestCreatedItem, setLatestCreatedItem] = useState(null);


  const fetchData = async () => {
    if (!queries || !queries.list) {
      setLoading(false);
      return;
    }

    try {
      const result = await client.graphql({
        query: queries.list,
        variables: {
          filter: dynamicFilter,
        },
      });

      const dynamicKey = Object.keys(result.data)[0];
      const fetchedData = result.data[dynamicKey].items;

      // Fetch user statuses
      const userStatusResult = await client.graphql({ query: listUserStatuses });
      const userStatuses = userStatusResult.data.listUserStatuses.items;

      // Fetch video conferences
      const videoConferenceResult = await client.graphql({ query: listTransactionVideoConferences });
      const videoConferences = videoConferenceResult.data.listTransactionVideoConferences.items;

      const enrichedData = fetchedData.map((transaction) => {
        const status = userStatuses.find(
          (u) => u.user_profile_id === transaction.user_profile_id
        );

        const userVCs = videoConferences.filter(
          (vc) =>
            vc.transaction_id === transaction.transaction_id &&
            vc.user_id === transaction.user_profile_id
        );

        return {
          ...transaction,
          user_status_updatedAt: status?.updatedAt || null,
          video_conferences: userVCs,
          video_conferences_updatedAt: userVCs[0]?.updatedAt,

        };
      });

      const sortedData = enrichedData.sort((a, b) =>
        new Date(b.createdAt) - new Date(a.createdAt)
      );

      setData(sortedData);
    } catch (error) {
      console.error(`Error fetching ${model}:`, error);
    } finally {
      setLoading(false);
    }
  };

  const refetch = () => {
    setLoading(true);
    fetchData();
  };

  useEffect(() => {
    if (queries && queries.list) fetchData();

    const unsubscribeFromUpdates = handleCRUD(
      client,
      subscriptions,
      setData,
      (deletedItem) => {
        setOnExternalDelete(deletedItem);
      },
      setLatestCreatedItem
    );

    // 🟡 Subscribe to user status updates
    const userStatusSub = client.graphql({ query: onUpdateUserStatus }).subscribe({
      next: ({ data }) => {
        const updatedStatus = data?.onUpdateUserStatus;
        if (!updatedStatus) return;

        setData((prevData) =>
          prevData.map((transaction) => {
            if (transaction.user_profile_id === updatedStatus.user_profile_id) {
              return {
                ...transaction,
                user_status: updatedStatus.user_status,
                user_status_updatedAt: updatedStatus.updatedAt,
              };
            }
            return transaction;
          })
        );
      },
      error: (error) => {
        console.warn("User status subscription error:", error);
      },
    });

    // 🟢 Subscribe to video conference creation
    const videoCreateSub = client.graphql({
      query: onCreateTransactionVideoConference
    }).subscribe({
      next: ({ data }) => {
        const newVC = data?.onCreateTransactionVideoConference;
        if (!newVC) return;

        setData((prevData) =>
          prevData.map((transaction) => {
            if (
              transaction.transaction_id === newVC.transaction_id &&
              transaction.user_profile_id === newVC.user_id
            ) {
              const updatedVCs = [...(transaction.video_conferences || []), newVC];
              return {
                ...transaction,
                video_conferences: updatedVCs,
                video_conferences_updatedAt: updatedVCs[0]?.updatedAt,

              };
            }
            return transaction;
          })
        );
      },
      error: (err) => console.warn("VC create subscription error:", err),
    });



    // 🔵 Subscribe to video conference updates
    const videoUpdateSub = client.graphql({
      query: onUpdateTransactionVideoConference,
    }).subscribe({
      next: ({ data }) => {
        const updatedVC = data?.onUpdateTransactionVideoConference;
        if (!updatedVC) return;

        setData((prevData) =>
          prevData.map((transaction) => {
            if (
              transaction.transaction_id === updatedVC.transaction_id &&
              transaction.user_profile_id === updatedVC.user_id
            ) {
              const updatedVCs = (transaction.video_conferences || []).map((vc) =>
                vc.id === updatedVC.id ? updatedVC : vc
              );

              return {
                ...transaction,
                video_conferences: updatedVCs,
                video_conferences_updatedAt: updatedVC?.updatedAt,
              };
            }
            return transaction;
          })
        );
      },
      error: (err) => console.warn("VC update subscription error:", err),
    });






    // 🔴 Subscribe to video conference deletion
    const videoDeleteSub = client.graphql({
      query: onDeleteTransactionVideoConference
    }).subscribe({
      next: ({ data }) => {
        const deletedVC = data?.onDeleteTransactionVideoConference;
        if (!deletedVC) return;

        setData((prevData) =>
          prevData.map((transaction) => {
            if (
              transaction.transaction_id === deletedVC.transaction_id &&
              transaction.user_profile_id === deletedVC.user_id
            ) {
              const remainingVCs = (transaction.video_conferences || []).filter(
                (vc) => vc.id !== deletedVC.id
              );
              return {
                ...transaction,
                video_conferences: remainingVCs,
                video_conferences_updatedAt: remainingVCs[0]?.updatedAt,

              };
            }
            return transaction;
          })
        );
      },
      error: (err) => console.warn("VC delete subscription error:", err),
    });

    return () => {
      unsubscribeFromUpdates();
      userStatusSub.unsubscribe();
      videoCreateSub.unsubscribe();
      videoDeleteSub.unsubscribe();
      videoUpdateSub.unsubscribe();
    };
  }, [dynamicFilter]);

  useEffect(() => {
    if (dataAmplify) {
      const messageSubscriptions = {};
      const processedMessageIds = new Set();

      const subscribeToMessages = (transaction) => {
        const subscription = client.graphql({
          query: onCreateChatMessage,
          variables: { transaction_id: transaction.transaction_id },
        }).subscribe({
          next: ({ data }) => {
            const newMessage = data.onCreateChatMessage;
            if (newMessage && !processedMessageIds.has(newMessage.id)) {
              processedMessageIds.add(newMessage.id);

              setData((prevData) =>
                prevData.map((item) => {
                  if (item.transaction_id === newMessage.transaction_id) {
                    return {
                      ...item,
                      TransactionMessagesRelation: {
                        ...item.TransactionMessagesRelation,
                        items: [
                          ...(item.TransactionMessagesRelation?.items || []),
                          newMessage,
                        ],
                      },
                    };
                  }
                  return item;
                })
              );
            }
          },
          error: (error) => console.warn(error),
        });

        messageSubscriptions[transaction.transaction_id] = subscription;
      };

      dataAmplify.forEach(subscribeToMessages);

      return () => {
        Object.values(messageSubscriptions).forEach((sub) => sub.unsubscribe());
      };
    }
  }, [dataAmplify]);

  const createItem = async (input) => {
    if (!queries?.create) return;
    try {
      const result = await client.graphql({
        query: queries.create,
        variables: { input },
      });
      const newItem = result.data[Object.keys(result.data)[0]];
      setData((prevData) => [newItem, ...prevData]);
      return newItem;
    } catch (error) {
      console.error(`Error creating ${model}:`, error);
    }
  };

  const updateItem = async (input) => {
    if (!queries?.update) return;
    try {
      const result = await client.graphql({
        query: queries.update,
        variables: { input },
      });
      const updatedItem = result.data[Object.keys(result.data)[0]];
      setData((prevData) =>
        prevData.map((item) => (item.id === updatedItem.id ? updatedItem : item))
      );
      return updatedItem;
    } catch (error) {
      console.error(`Error updating ${model}:`, error);
    }
  };

  const deleteItem = async (id) => {
    if (!queries?.delete) {
      console.warn("Delete mutation is not defined.");
      return;
    }

    try {
      const result = await client.graphql({
        query: queries.delete,
        variables: { input: { id } },
      });
      const deletedItem = result.data[Object.keys(result.data)[0]];
      if (deletedItem?.id) {
        setData((prevData) => prevData.filter((item) => item.id !== deletedItem.id));
        return deletedItem.id;
      }
    } catch (error) {
      console.error(`❌ Error deleting ${model}:`, error);
      throw error;
    }
  };

  const checkItemExists = async (query, filter) => {
    const variables = { filter };
    try {
      const checkResponse = await client.graphql({ query, variables });
      const itemsKey = Object.keys(checkResponse.data)[0];
      const items = checkResponse.data[itemsKey].items;
      return items.length > 0 ? items[0] : null;
    } catch (error) {
      console.error('Error checking item existence:', error);
      throw error;
    }
  };

  const clearData = () => setData([]);

  const updateFilter = (newFilter) => {
    setDynamicFilter((prevFilter) => ({ ...prevFilter, ...newFilter }));
  };

  return {
    dataAmplify,
    loadingAmplify,
    createItem,
    updateItem,
    deleteItem,
    checkItemExists,
    refetch,
    clearData,
    updateFilter,
    setData,
    onExternalDelete,
    latestCreatedItem
  };
};

export default useModelData;
